'use client';

import { Stack, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Autoplay, EffectFade, Parallax } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types';

import { projects } from '@/assets/projects';
import HexagonalButton from '@/components/bases/HexagonalButton';

interface Project {
  id: number | string;
  name: string;
  image: string;
  href: string;
}

export const FeaturedProjectsSection = () => {
  const router = useRouter();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const projectsData: Project[] = [
    {
      id: 1,
      name: 'TIKTRIX',
      image: projects.project_1,
      href: 'https://node.tiktrix.gg/',
    },
    {
      id: 2,
      name: 'VALLEY ECOSYSTEM',
      image: projects.project_2,
      href: 'https://node.valleyecosystem.com/',
    },
    {
      id: 4,
      name: 'NODE DASHBOARD',
      image: projects.project_4,
      href: 'https://lfg.nodes.magnetohr.com/',
    },
    {
      id: 5,
      name: 'NODE DASHBOARD',
      image: projects.project_5,
      href: 'https://get.flipit.com/',
    },
    {
      id: 6,
      name: 'HELIX Aegis Node',
      image: projects.project_6,
      href: 'https://node.helixgames.gg/',
    },
  ];

  // Define Swiper breakpoints with type
  const breakpoints: SwiperOptions['breakpoints'] = {
    640: {
      slidesPerView: 1,
      spaceBetween: 30,
    },
    768: {
      slidesPerView: 2,
      spaceBetween: 30,
    },
    1024: {
      slidesPerView: 2,
      spaceBetween: 30,
    },
  };

  return (
    <Stack
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      paddingY={{ xs: '32px', md: '80px' }}
      alignItems={'center'}
      component={'section'}
      position={'relative'}
      sx={{ background: 'linear-gradient(180deg, #1C2B44 0%, #060912 101.01%)' }}>
      <Stack
        bgcolor={'#060912'}
        className={'bottom-cut'}
        position={'absolute'}
        top={0}
        zIndex={1}
        left={'50%'}
        height={{ xs: '12px', md: '30px' }}
        width={{ xs: 'min(160px, 100%)', md: 'min(616px, 100%)' }}
        sx={{ transform: 'translateX(-50%)' }}
      />
      <Stack gap={{ xs: 4, md: 7 }} width={'min(1280px, 100%)'} zIndex={2} padding={{ xs: '24px', md: '80px' }}>
        <Stack gap={2} width={'100%'} textAlign={'center'}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            fontSize={{ xs: '24px', md: '48px' }}
            lineHeight={{ xs: '32px', md: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            FEATURED PROJECTS
          </Typography>
          <Typography
            fontSize={{ xs: '14px', md: '18px' }}
            component={'h4'}
            variant={'bodyLarge'}
            color={'#FFFFFF'}
            className={'mix-blend'}>
            Get inspired by successful core sales and discover new opportunities in the ecosystem.
          </Typography>
        </Stack>

        {isClient && (
          <Swiper
            centeredSlides={false}
            loop={true}
            initialSlide={0}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            speed={1000}
            modules={[Autoplay, Parallax, EffectFade]}
            className='mySwiper'
            breakpoints={breakpoints}>
            {projectsData.map(project => (
              <SwiperSlide key={project.id}>
                <a href={project.href} target={'_blank'} rel='noreferrer'>
                  <Stack
                    width={'100%'}
                    height={{ xs: '184px', md: '360px' }}
                    sx={{
                      backgroundImage: `url(${project.image})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.03)',
                      },
                    }}
                  />
                </a>
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        <Stack alignItems={'center'} justifyContent={'center'}>
          <Stack width={275} display={{ xs: 'none', md: 'block' }}>
            <HexagonalButton
              variant={'secondary'}
              label={'EXPLORE CORE SALES'}
              onClick={() => router.push('/explore')}
            />
          </Stack>
          <Stack width={202} display={{ xs: 'block', md: 'none' }}>
            <HexagonalButton
              variant={'secondary'}
              size={'small'}
              label={'EXPLORE CORE SALES'}
              onClick={() => router.push('/explore')}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
